import React, { useMemo } from 'react';
import { GRCControl, Framework } from '../../types/grc';
import { AlertTriangle, Gavel, AlertOctagon, ExternalLink, ChevronRight, ShieldAlert } from 'lucide-react';

interface PenaltyAlertsProps {
  controls: GRCControl[];
  framework: Framework;
  onControlClick?: (control: GRCControl) => void;
}

// Penalty information by framework
const PENALTY_INFO: Record<string, {
  lawReference: string;
  maxFine: string;
  currency: string;
  article: string;
  description: string;
}> = {
  'ZIB': {
    lawReference: 'Zakon o informacionoj bezbednosti ("Sl. glasnik RS", br. 91/2025)',
    maxFine: '2.000.000',
    currency: 'RSD',
    article: 'Članovi 34-37',
    description: 'Privredni prestupi i prekršaji za nepoštovanje mera informacione bezbednosti'
  },
  'GDPR': {
    lawReference: 'Opšta uredba o zaštiti podataka (EU) 2016/679',
    maxFine: '20.000.000',
    currency: 'EUR',
    article: 'Član 83',
    description: 'Administrativne kazne za povrede zaštite podataka o ličnosti'
  },
  'NIS2': {
    lawReference: 'Direktiva (EU) 2022/2555',
    maxFine: '10.000.000',
    currency: 'EUR',
    article: 'Član 34',
    description: 'Kazne za neusklađenost sa NIS2 direktivom'
  },
  'PCI-DSS': {
    lawReference: 'PCI DSS v4.0.1 - Payment Card Industry Data Security Standard',
    maxFine: '100.000',
    currency: 'USD',
    article: 'Contractual',
    description: 'Novčane kazne od kartičarskih brendova za neusklađenost'
  }
};

export const PenaltyAlerts: React.FC<PenaltyAlertsProps> = ({
  controls,
  framework,
  onControlClick
}) => {
  // Find non-compliant critical controls (potential penalty triggers)
  const penaltyTriggers = useMemo(() => {
    return controls.filter(c => {
      // Non-compliant = not assessed, non_compliant, or partial
      const isNonCompliant = c.status === 'non_compliant' || 
                             c.status === 'partial' ||
                             c.status === 'not_assessed';
      
      // Only critical and high severity for penalties
      const isCritical = c.severity === 'critical' || c.severity === 'high';
      
      // Has penalty mention in guidance
      const hasPenaltyGuidance = c.guidance?.toLowerCase().includes('upozorenje') ||
                                  c.guidance?.toLowerCase().includes('kazn') ||
                                  c.guidance?.toLowerCase().includes('penalt') ||
                                  c.title?.toLowerCase().includes('kazn');
      
      // applicable: 1 = applicable, 2 = partial
      const isApplicable = c.applicable === 1 || c.applicable === 2;
      
      return isNonCompliant && isApplicable && (isCritical || hasPenaltyGuidance);
    });
  }, [controls]);

  // Get penalty info for this framework
  const penaltyInfo = PENALTY_INFO[framework.code] || null;

  // Don't show if no penalty triggers
  if (penaltyTriggers.length === 0 && !penaltyInfo) {
    return null;
  }

  // Calculate estimated penalty exposure
  const criticalCount = penaltyTriggers.filter(c => c.severity === 'critical').length;
  const highCount = penaltyTriggers.filter(c => c.severity === 'high').length;
  const estimatedRisk = criticalCount > 0 ? 'Kritičan' : highCount > 2 ? 'Visok' : 'Srednji';

  return (
    <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-xl border border-red-200 dark:border-red-800 overflow-hidden">
      {/* Header */}
      <div className="bg-red-100 dark:bg-red-900/40 px-6 py-4 border-b border-red-200 dark:border-red-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500 dark:bg-red-600 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-red-800 dark:text-red-200">
                ⚠️ Upozorenje: Potencijalne kazne
              </h3>
              <p className="text-sm text-red-600 dark:text-red-300">
                Identifikovane neusklađenosti koje mogu rezultirati novčanim kaznama
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <div className={`
              px-3 py-1 rounded-full text-sm font-bold
              ${estimatedRisk === 'Kritičan' ? 'bg-red-500 text-white' : ''}
              ${estimatedRisk === 'Visok' ? 'bg-orange-500 text-white' : ''}
              ${estimatedRisk === 'Srednji' ? 'bg-amber-500 text-white' : ''}
            `}>
              Rizik: {estimatedRisk}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Penalty Info Box */}
        {penaltyInfo && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4 border border-red-200 dark:border-red-700">
            <div className="flex items-start gap-3">
              <Gavel className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                  {penaltyInfo.lawReference}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  {penaltyInfo.description}
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Maksimalna kazna: </span>
                    <span className="font-bold text-red-600 dark:text-red-400">
                      do {penaltyInfo.maxFine} {penaltyInfo.currency}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Pravni osnov: </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {penaltyInfo.article}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Penalty Triggers */}
        {penaltyTriggers.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <AlertOctagon className="w-4 h-4 text-red-500" />
              Neusklađene kontrole ({penaltyTriggers.length})
            </h4>
            
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {penaltyTriggers.slice(0, 10).map((control) => (
                <div
                  key={control.id}
                  onClick={() => onControlClick?.(control)}
                  className={`
                    bg-white dark:bg-gray-800 rounded-lg p-3 border cursor-pointer
                    hover:border-red-400 dark:hover:border-red-500 transition-colors
                    ${control.severity === 'critical' 
                      ? 'border-red-300 dark:border-red-700' 
                      : 'border-orange-200 dark:border-orange-700'
                    }
                  `}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm text-gray-500 dark:text-gray-400">
                          {control.control_code}
                        </span>
                        <span className={`
                          px-2 py-0.5 rounded text-xs font-semibold uppercase
                          ${control.severity === 'critical' 
                            ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300' 
                            : 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
                          }
                        `}>
                          {control.severity === 'critical' ? 'KRITIČNO' : 'VISOKO'}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {control.title}
                      </p>
                      {control.guidance?.includes('UPOZORENJE') && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {control.guidance.split('UPOZORENJE:')[1]?.split('.')[0]?.trim() || 
                           control.guidance.split('.')[0]}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                </div>
              ))}
              
              {penaltyTriggers.length > 10 && (
                <div className="text-center py-2 text-sm text-gray-500 dark:text-gray-400">
                  + još {penaltyTriggers.length - 10} neusklađenih kontrola
                </div>
              )}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-700">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              ℹ️ Implementirajte kritične kontrole da smanjite rizik od kazni
            </p>
            <button 
              onClick={() => onControlClick?.(penaltyTriggers[0])}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Reši odmah
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PenaltyAlerts;
